import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { useCurator } from '../contexts/CuratorContext'
import { api } from '../services/api'

/**
 * Единый инбокс комментариев (CommentsInboxSheet) на клиентских экранах —
 * Дневник, программа, активная тренировка: данные, прочтение и ответ.
 *
 * Куратор, открывший клиента (`enterClient`), видит эти экраны с токеном
 * просмотра, а сервер режет любой не-GET с `curatorId` в JWT (403). Клиентские
 * `POST /diary/comment` и `/diary/all-comments/mark-read` от его имени молча
 * падали — ответ куратора пропадал. Теперь у куратора шторка в его
 * перспективе («Чат с клиентом»), а ответ уходит кураторским
 * `POST /curator/comments`.
 *
 * Выдача — клиентская `GET /diary/all-comments` токеном просмотра: при
 * имперсонации сервер не отмечает в ней непрочитанных, а у веток замеров и
 * фото нет цели ответа — им куратор отвечает из «Чата с клиентом» в профиле.
 * Прочтение в просмотре не шлём: сообщения клиента куратор гасит там же.
 *
 * Админ «за пользователя» видит клиентский инбокс и ничего не пишет:
 * «Ответить» гасит тостом сама шторка.
 */
export function useCommentsInbox(opts: { enabled?: boolean; invalidateOnReply?: QueryKey[] } = {}) {
  const { enabled = true, invalidateOnReply = [] } = opts
  const { viewingClientId, isAdminView } = useCurator()
  const queryClient = useQueryClient()
  const isCuratorViewing = !!viewingClientId && !isAdminView

  const { data } = useQuery({
    queryKey: ['diary', 'all-comments'],
    queryFn: () => api.getAllComments().catch(() => ({ comments: [] })),
    staleTime: 30_000,
    enabled,
  })

  /** Открытие инбокса = прочтение всех; гасим красные счётчики/бейджи. */
  const markRead = () => {
    if (viewingClientId) return
    api.markAllCommentsRead()
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['diary', 'all-comments'] })
        queryClient.invalidateQueries({ queryKey: ['diary', 'unread-comments-count'] })
        queryClient.invalidateQueries({ queryKey: ['diary', 'unread-comments-by-date'] })
      })
      .catch(() => {})
  }

  const reply = async (target: { targetType: string; targetId: string }, text: string) => {
    const body = { targetType: target.targetType, targetId: target.targetId, text }
    await (isCuratorViewing ? api.createCuratorComment(body) : api.postClientComment(body))
    queryClient.invalidateQueries({ queryKey: ['diary', 'all-comments'] })
    for (const key of invalidateOnReply) queryClient.invalidateQueries({ queryKey: key })
  }

  return {
    comments: data?.comments ?? [],
    markRead,
    reply,
    perspective: (isCuratorViewing ? 'CURATOR' : 'CLIENT') as 'CLIENT' | 'CURATOR',
  }
}
